import {
  AUTO_ACK_USER_KEY,
  AUTO_ACK_USER_TYPES,
  disciplineOfPatientKey,
  patientKeyOfDiscipline,
  IAutoAckEvent,
  IDepartmentData,
  IUserInfo,
  USER_STATUS,
  USER_TYPE,
  usersMatchingCriteria,
} from "@home-health-notify/domain";
import { IAutoAckCriteria } from "@home-health-notify/models";

// The app-wide display name — "First Last - SHORTHAND", built by
// UserInfoMapper.makeUserTitle and used by the directory, member lists and the
// add-member pickers. Auto Ack must not invent its own format.
export const userLabel = (user: IUserInfo): string => user.title || user.name;

export const selectableUserTypes = AUTO_ACK_USER_TYPES;

// Patients and app accounts are never asked. Blocked and deleted users are
// kept so a rule that names one can still be read back and cleaned up — see
// matchedUsers for where they stop counting.
export const selectableUsers = (users: IUserInfo[]): IUserInfo[] =>
  users
    .filter(
      user => user.type !== USER_TYPE.APP && user.type !== USER_TYPE.PATIENT,
    )
    .sort((a, b) => a.name.localeCompare(b.name));

// The colouring is what separates someone who will never be asked from the
// people who will.
export const userStatusSx = (user: IUserInfo): object => {
  if (user.status === USER_STATUS.DELETED)
    return { color: "error.main", textDecoration: "line-through" };
  if (user.status === USER_STATUS.BLOCKED) return { color: "warning.main" };
  return {};
};

export const userChipProps = (user: IUserInfo) => {
  if (user.status === USER_STATUS.DELETED)
    return {
      color: "error" as const,
      variant: "outlined" as const,
      sx: { "& .MuiChip-label": { textDecoration: "line-through" } },
    };
  if (user.status === USER_STATUS.BLOCKED)
    return { color: "warning" as const, variant: "outlined" as const };
  return {};
};

// A status suffix carries the same meaning as the colour for anyone who can't
// rely on it.
const withStatus = (
  base: string,
  user: IUserInfo,
  label: (key: string, options?: object) => string,
): string => {
  if (user.status === USER_STATUS.DELETED)
    return `${base} (${label("statusDeleted")})`;
  if (user.status === USER_STATUS.BLOCKED)
    return `${base} (${label("statusBlocked")})`;
  return base;
};

export const userLabelWithStatus = (
  user: IUserInfo,
  label: (key: string, options?: object) => string,
): string => withStatus(userLabel(user), user, label);

// APP and PATIENT never reach these lists, so the four remaining types all have
// a label; anything else is left off rather than rendered as a missing key.
const userTypeLabel = (
  user: IUserInfo,
  label: (key: string, options?: object) => string,
): string =>
  AUTO_ACK_USER_TYPES.includes(user.type) ? label(`userType.${user.type}`) : "";

// The directory title is "First Last - RN", where RN is a designation. Two
// people can share both, so where there is only one line to work with the type
// rides along behind it.
export const userLabelWithTypeAndStatus = (
  user: IUserInfo,
  label: (key: string, options?: object) => string,
): string => {
  const type = userTypeLabel(user, label);
  return withStatus(
    type ? `${userLabel(user)} · ${type}` : userLabel(user),
    user,
    label,
  );
};

// The picker has room for a second line, so type and department go there rather
// than crowding the name.
export const userSecondaryLine = (
  user: IUserInfo,
  label: (key: string, options?: object) => string,
): string =>
  [userTypeLabel(user, label), user.department?.name]
    .filter(Boolean)
    .join(" · ");

export const sortedEvents = (events: IAutoAckEvent[]): IAutoAckEvent[] =>
  [...events].sort((a, b) =>
    a.category === b.category
      ? a.displayName.localeCompare(b.displayName)
      : a.category.localeCompare(b.category),
  );

export const departmentLabel = (
  id: string,
  departments: IDepartmentData[],
): string => departments.find(department => department.id === id)?.name ?? id;

export const describePatientCriteria = (
  groups: IAutoAckCriteria[][] | undefined,
  label: (key: string, options?: object) => string,
): string => {
  const disciplines = (groups ?? [])
    .flat()
    .map(criteria => disciplineOfPatientKey(criteria.key));
  if (!disciplines.length) return "";
  return label("forPatientsStarting", {
    names: disciplines.join(label("joinDisciplines")),
  });
};

// A patient's start of care is by one discipline, so disciplines have to be
// ORed: each gets its own group. Putting them in a single AND group passes
// backend validation and then silently matches nobody.
export const buildPatientCriteria = (
  disciplines: string[],
): IAutoAckCriteria[][] =>
  disciplines.map(discipline => [
    { key: patientKeyOfDiscipline(discipline), values: ["true"] },
  ]);

const isInactive = (user: IUserInfo): boolean =>
  user.status === USER_STATUS.BLOCKED || user.status === USER_STATUS.DELETED;

const namedIds = (groups: IAutoAckCriteria[][] | undefined): Set<string> =>
  new Set(
    (groups ?? [])
      .flat()
      .filter(criteria => criteria.key === AUTO_ACK_USER_KEY.USER_ID)
      .flatMap(criteria => criteria.values ?? []),
  );

// A blocked or deleted person belongs on screen only where a rule names them
// outright, because that is the configuration an admin has to go and fix.
// Letting them fall out of a type, department or name match instead would pad
// every count with people the watcher is never going to ask.
export const matchedUsers = (
  groups: IAutoAckCriteria[][] | undefined,
  users: IUserInfo[],
): IUserInfo[] => {
  const named = namedIds(groups);
  return usersMatchingCriteria(groups, users).filter(
    user => !isInactive(user) || named.has(user.id),
  );
};
